import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Factory, Eye, Edit2, ChevronRight, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { productionLotService, productService, productRevisionService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'closed', label: 'Closed' },
];

export default function ProductionLotsPage() {
  const [lots, setLots] = useState([]);
  const [products, setProducts] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    product_id: '',
    product_revision_id: '',
    quantity: '',
    opening_date: new Date().toISOString().split('T')[0],
    responsible_operator: '',
    current_machine: '',
    notes: ''
  });

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    loadLots();
  }, [search, filterStatus]);

  const loadProducts = async () => {
    try {
      const response = await productService.getAll({ status: 'active' });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products');
    }
  };

  const loadRevisions = async (productId) => {
    try {
      const response = await productRevisionService.getAll({ product_id: productId });
      setRevisions(response.data);
    } catch (error) {
      console.error('Failed to load revisions');
    }
  };

  const loadLots = async () => {
    try {
      const params = { search };
      if (filterStatus) params.status = filterStatus;
      const response = await productionLotService.getAll(params);
      setLots(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento lotti');
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteLot = async (lot) => {
    if (!window.confirm(`Eliminare il lotto ${lot.lot_code}? Verranno eliminati anche avanzamento e dati utensili associati.`)) return;
    try {
      await productionLotService.delete(lot.id);
      toast.success('Lotto eliminato');
      loadLots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella cancellazione');
    }
  };

  const handleProductChange = (productId) => {
    setFormData({ ...formData, product_id: productId, product_revision_id: '' });
    loadRevisions(productId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        product_id: formData.product_id,
        product_revision_id: formData.product_revision_id || null,
        quantity: parseInt(formData.quantity),
        opening_date: formData.opening_date,
        responsible_operator: formData.responsible_operator || null,
        current_machine: formData.current_machine || null,
        notes: formData.notes || null
      };
      await productionLotService.create(data);
      toast.success('Lotto di produzione creato');
      setDialogOpen(false);
      resetForm();
      loadLots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: '',
      product_revision_id: '',
      quantity: '',
      opening_date: new Date().toISOString().split('T')[0],
      responsible_operator: '',
      current_machine: '',
      notes: ''
    });
    setRevisions([]);
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-sky-500 text-white',
      in_progress: 'bg-amber-500 text-white',
      suspended: 'bg-red-500 text-white',
      closed: 'bg-slate-400 text-white',
    };
    return styles[status] || styles.open;
  };

  return (
    <div className="space-y-6" data-testid="production-lots-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lotti di Produzione</h1>
          <p className="text-sm text-slate-500 mt-1">Gestisci i lotti di produzione e monitora l'avanzamento</p>
        </div>
        {hasRole('admin', 'quality', 'production') && (
          <Button onClick={openNewDialog} className="h-12 px-6 text-base" data-testid="add-lot-btn">
            <Plus className="w-5 h-5 mr-2" />
            Nuovo Lotto
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per codice lotto o Lotto Cliente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-lots-input"
              />
            </div>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : lots.length === 0 ? (
            <div className="text-center py-12">
              <Factory className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No production lots found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Codice Lotto</th>
                    <th>Prodotto</th>
                    <th>Cliente</th>
                    <th>Ordine</th>
                    <th>Lotto Cliente</th>
                    <th>Qtà</th>
                    <th>Buoni</th>
                    <th>Scarti</th>
                    <th>Apertura</th>
                    <th>Stato</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lots.map((lot) => (
                    <tr key={lot.id} className="row-link" onClick={() => navigate(`/production-lots/${lot.id}`)} data-testid={`lot-row-${lot.id}`}>
                      <td className="font-mono font-medium">{lot.lot_code}</td>
                      <td>
                        <div>
                          <p className="font-mono">{lot.product_code}</p>
                          <p className="text-xs text-slate-500">{lot.product_name}</p>
                        </div>
                      </td>
                      <td>{lot.client_name || '-'}</td>
                      <td className="font-mono">{lot.order_number || '-'}</td>
                      <td className="font-mono text-xs" data-testid={`lot-customer-lot-${lot.id}`}>
                        {lot.customer_lot_snapshot || <span className="text-slate-300">-</span>}
                      </td>
                      <td className="font-semibold">{lot.quantity}</td>
                      <td className="text-emerald-600 font-semibold">{lot.good_quantity || 0}</td>
                      <td className="text-rose-600 font-semibold">{lot.scrap_quantity || 0}</td>
                      <td>{lot.opening_date}</td>
                      <td>
                        <Badge className={getStatusBadge(lot.status)}>
                          {lot.status?.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {hasRole('admin') && (
                            <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => handleDeleteLot(lot)} data-testid={`delete-lot-${lot.id}`}>
                              <Trash2 className="w-4 h-4 text-rose-500" />
                            </Button>
                          )}
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Lotto di Produzione</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Product *</Label>
                <Select
                  value={formData.product_id}
                  onValueChange={handleProductChange}
                  required
                >
                  <SelectTrigger data-testid="lot-product-select">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.code} - {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {revisions.length > 0 && (
                <div className="space-y-2">
                  <Label>Revision</Label>
                  <Select
                    value={formData.product_revision_id}
                    onValueChange={(value) => setFormData({ ...formData, product_revision_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select revision" />
                    </SelectTrigger>
                    <SelectContent>
                      {revisions.map((rev) => (
                        <SelectItem key={rev.id} value={rev.id}>
                          {rev.revision_code} {rev.is_active && '(Active)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    required
                    min="1"
                    data-testid="lot-quantity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Opening Date *</Label>
                  <Input
                    type="date"
                    value={formData.opening_date}
                    onChange={(e) => setFormData({ ...formData, opening_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Responsible Operator</Label>
                  <Input
                    value={formData.responsible_operator}
                    onChange={(e) => setFormData({ ...formData, responsible_operator: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Machine</Label>
                  <Input
                    value={formData.current_machine}
                    onChange={(e) => setFormData({ ...formData, current_machine: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="h-11 px-5" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="h-11 px-6" data-testid="save-lot-btn">Crea Lotto</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
